import { clamp, cos, float, length, max, mix, sin, uv, vec2 } from 'three/tsl';

import { NEAR_ZERO_3 } from '@/app/lcv/components/sections/spartan/shared/webgpu/constants';

// RAKING THE PATTERN INSTEAD OF THE PLANE.
//
// The ground used to be a 60-unit quad rotated 129 degrees about X and -40
// about Z: a real plane, really tilted, seen in real perspective. That
// reads correctly but it cannot be made full-bleed. A tilted quad meets the
// camera frustum in a trapezium, so covering the whole screen means growing
// it until the near edge is enormous and the far edge is past the horizon
// -- and every pixel of that growth is shaded, most of it off-screen.
//
// So the plane is now a flat quad sized exactly to the viewport, and the
// RAKE HAS MOVED INTO THE SHADER. This maps each screen pixel onto a
// virtual floor, which is the same projection the geometry was getting from
// the camera, done per pixel instead of per vertex. The picture is the
// same; what changes is that the quad is always exactly the screen, so
// there is nothing off-screen to shade and nothing to re-fit per aspect.
//
// WHY EVERY CONSUMER TAKES THE RESULT. The lattice is the obvious one, but
// the idle wave and the reveal sweep both used to sample `positionLocal.xy`
// -- the plane's own surface coordinates. On a camera-facing quad those are
// screen coordinates, so left alone the wave would travel across the SCREEN
// at a constant rate rather than across the FLOOR with the foreshortening
// the rest of the picture has. They all read `coord` for that reason.

export interface GroundProjectionParams {
  // In-plane rotation of the whole pattern, radians. This is what the old
  // pose's Z rotation did.
  rollRad: any;
  // HOW RAKED THE FLOOR IS, 0..1. At 0 the pattern is flat-on and this
  // whole transform collapses to the identity, which is the un-projected
  // look. At 1 it is a full floor receding to `horizon`.
  //
  // A blend rather than an angle because the two ends are different
  // projections, not two values of one: there is no finite camera pitch
  // that gives a flat-on grid, and no scaling of a flat grid that gives a
  // horizon. Interpolating the COORDINATE is what makes the dial
  // continuous across both.
  pitch: any;
  // Where the horizon sits in centred screen Y (-1 bottom, +1 top). The
  // floor occupies everything below it.
  horizon: any;
  // How softly the floor dissolves as it reaches the horizon. Without it
  // the lattice aliases into noise in the last few pixels, where an
  // unbounded number of cells fall into each one.
  horizonFeather: any;
  // Pan across the floor. `originY` is the useful one -- it walks the
  // pattern toward or away from the viewer.
  originX: any;
  originY: any;
  // Overall pattern scale. Larger spreads the cells out.
  scale: any;
  // Viewport aspect (width / height), so a cell stays square on a wide
  // screen instead of being stretched with the quad.
  aspect: any;
}

export interface GroundProjection {
  // Floor-space coordinates. Unbounded: near the horizon they run away to
  // very large values, which is exactly what makes the cells compress.
  coord: any;
  // 0 at the bottom of the screen, 1 at the horizon. Bounded, so it can
  // drive a fade directly -- unlike the raw depth, which is unbounded.
  distance: any;
  // 1 on the floor, 0 above the horizon. Everything the ground draws is
  // multiplied by this, so the sky half is genuinely not drawn rather than
  // drawn and then hidden.
  mask: any;
}

// `compress` is the grid's own rim-tightening, applied BEFORE the
// projection: it is a property of the lattice (cells bunching toward the
// edge of the pattern), whereas the projection is a property of the view.
// Applying it after would have the perspective stretch the compression
// itself, which reads as the grid warping rather than receding.
const tightenTowardRim = (axis: any, compression: any): any =>
  axis.mul(mix(float(1), axis.abs(), clamp(compression, 0, 0.99)));

export const groundProjection = (
  projection: GroundProjectionParams,
  compression: any,
  screenUV: any = uv()
): GroundProjection => {
  const {
    rollRad,
    pitch,
    horizon,
    horizonFeather,
    originX,
    originY,
    scale,
    aspect,
  } = projection;

  // Centred screen coordinates, -1..1, with X widened by the aspect so the
  // pattern is measured in square units rather than in fractions of a
  // rectangle.
  const centred: any = screenUV.sub(0.5).mul(2);
  const screenX: any = centred.x.mul(aspect);
  const screenY: any = centred.y;

  // ROLL, in screen space and before the rake -- the same order the old
  // pose applied its Z rotation in.
  const c: any = cos(rollRad);
  const s: any = sin(rollRad);
  const rolledX: any = screenX.mul(c).sub(screenY.mul(s));
  const rolledY: any = screenX.mul(s).add(screenY.mul(c));

  const flatX: any = tightenTowardRim(rolledX, compression);
  const flatY: any = tightenTowardRim(rolledY, compression);

  // THE RAKE, AS A HOMOGENEOUS DIVIDE.
  //
  // `gap` is how far below the horizon this pixel sits, and `nearGap` is
  // that same distance at the BOTTOM of the screen -- the nearest floor
  // there is. Their ratio is the perspective `w`: 1 at the near edge,
  // falling to 0 at the horizon. Dividing the flat coordinate by it is
  // exactly what a projection matrix does.
  //
  // WHY A DIVIDE RATHER THAN A BLEND BETWEEN TWO COORDINATES. The obvious
  // version -- lerp from "flat xy" toward "floor xy" -- is wrong in a way
  // that is not obvious until it is on screen: the flat coordinate is -1
  // at the bottom of the frame while a depth is always positive, so the
  // mix crosses zero and the whole pattern slides and flips through itself
  // as `pitch` is dialled. Interpolating `w` instead leaves the near edge
  // a FIXED POINT: at any pitch, the bottom row of pixels maps to the same
  // floor coordinate it had flat-on, and raising the pitch only compresses
  // what is further away. That is also what keeps `gridCount` meaning the
  // same thing at every pitch.
  //
  // Clamped off zero because the horizon is infinitely far away and the
  // divide would blow up exactly on that line.
  const gap: any = max(horizon.sub(rolledY), float(NEAR_ZERO_3));
  const nearGap: any = max(horizon.add(float(1)), float(NEAR_ZERO_3));

  // At pitch 0 this is a constant 1, so the divide below is the identity
  // and the pattern is exactly the un-raked lattice.
  const perspectiveW: any = max(
    mix(float(1), gap.div(nearGap), pitch),
    float(NEAR_ZERO_3)
  );

  const coord: any = vec2(
    flatX.div(perspectiveW).mul(scale).add(originX),
    flatY.div(perspectiveW).mul(scale).add(originY)
  );

  // 0 at the near edge, 1 at the horizon.
  //
  // Derived from `gap` rather than from `coord`, because `coord` is
  // unbounded up there: a fade driven by it would finish long before the
  // horizon did, and -- more to the point -- the lattice's precision dies
  // near the horizon (fract() of a very large number is noise), so the
  // fade is what has to reach 1 first. See `fadeEnd`.
  const projectedDistance: any = clamp(float(1).sub(gap.div(nearGap)), 0, 1);

  // FLAT-ON, DISTANCE IS RADIAL -- and NORMALISED so that 1 is the corner
  // of the screen, at any aspect.
  //
  // Without the normalisation this ran to whatever `length(aspect, 1)`
  // happened to be -- about 2.2 on a 16:9 monitor once the rim
  // compression had stretched X -- while `fadeEnd` is authored as a small
  // number around 0.7. Everything outside a modest central ellipse was
  // therefore fully faded, which reads exactly like the grid receding into
  // a hole rather than filling the frame.
  //
  // Dividing by the corner puts `fadeEnd` back on the scale it is written
  // on: a fraction of the way out, where 1 reaches the corners and
  // anything above that never finishes fading at all. It also makes the
  // dial mean the same thing on a phone and on an ultrawide, which the raw
  // length did not.
  const cornerRadius: any = max(
    length(vec2(tightenTowardRim(aspect, compression), float(1))),
    float(NEAR_ZERO_3)
  );
  const flatDistance: any = length(vec2(flatX, flatY)).div(cornerRadius);

  const distance: any = mix(flatDistance, projectedDistance, pitch);

  // Only the raked version has a horizon to cut against; flat-on there is
  // no sky, so the mask has to blend to a plain 1 or the pattern would
  // lose its top half at pitch 0.
  const horizonMask: any = clamp(
    horizon.sub(rolledY).div(max(horizonFeather, float(NEAR_ZERO_3))),
    0,
    1
  );
  const mask: any = mix(float(1), horizonMask, pitch);

  return { coord, distance, mask };
};
